// Word-level diffing, and grouping of the edits into changes that can be explained. Dependency-
// free, like text.ts, so every client can use it.

export type DiffSegmentType = 'equal' | 'insert' | 'delete';

export interface DiffSegment {
  type: DiffSegmentType;
  text: string;
}

const tokenize = (text: string): string[] =>
  text.match(/\s+|[\p{L}\p{N}\p{M}_]+|[^\s\p{L}\p{N}\p{M}_]/gu) ?? [];

// Past this many token comparisons, the changed middle is replaced wholesale.
const MAX_TABLE_CELLS = 250_000;

/**
 * Word-level diff between an original text and its rewrite. A bounded LCS keeps long pasted
 * documents responsive: for very large changed sections it preserves the shared edges and
 * replaces the middle.
 */
export function diffText(original: string, rewritten: string): DiffSegment[] {
  const before = tokenize(original);
  const after = tokenize(rewritten);
  const result: DiffSegment[] = [];
  const append = (type: DiffSegmentType, text: string | undefined) => {
    if (!text) return;
    const previous = result.at(-1);
    if (previous?.type === type) previous.text += text;
    else result.push({ type, text });
  };

  let start = 0;
  while (start < before.length && start < after.length && before[start] === after[start]) start++;
  let endBefore = before.length;
  let endAfter = after.length;
  while (endBefore > start && endAfter > start && before[endBefore - 1] === after[endAfter - 1]) {
    endBefore--;
    endAfter--;
  }

  append('equal', before.slice(0, start).join(''));
  const a = before.slice(start, endBefore);
  const b = after.slice(start, endAfter);
  if (a.length * b.length > MAX_TABLE_CELLS) {
    append('delete', a.join(''));
    append('insert', b.join(''));
  } else {
    const width = b.length + 1;
    const table = new Uint32Array((a.length + 1) * width);
    const lcs = (i: number, j: number): number => table[i * width + j] ?? 0;
    for (let i = a.length - 1; i >= 0; i--) {
      for (let j = b.length - 1; j >= 0; j--) {
        table[i * width + j] =
          a[i] === b[j] ? lcs(i + 1, j + 1) + 1 : Math.max(lcs(i + 1, j), lcs(i, j + 1));
      }
    }
    let i = 0;
    let j = 0;
    while (i < a.length || j < b.length) {
      if (i < a.length && j < b.length && a[i] === b[j]) {
        append('equal', a[i++]);
        j++;
      } else if (i < a.length && (j === b.length || lcs(i + 1, j) >= lcs(i, j + 1))) {
        append('delete', a[i++]);
      } else {
        append('insert', b[j++]);
      }
    }
  }
  append('equal', before.slice(endBefore).join(''));
  return result;
}

/** A group of nearby edits: what a stretch of the original became in the rewrite. */
export interface Change {
  /** The words as they were; empty for pure additions. */
  readonly before: string;
  /** The words as they are now; empty for pure removals. */
  readonly after: string;
  /** Indexes of the group's first and last diff segments, for marking the diff. */
  readonly first: number;
  readonly last: number;
}

/** Most changes one explanation request covers. */
export const MAX_EXPLAINED_CHANGES = 40;

const countWordTokens = (text: string) => text.match(/[\p{L}\p{N}]+/gu)?.length ?? 0;

function group(segments: readonly DiffSegment[], joinGap: number): Change[] {
  const changes: Change[] = [];
  let open: { before: string; after: string; first: number; last: number } | null = null;
  // Unchanged text after the open change, joined to it if another edit follows closely.
  let gap = '';

  const close = () => {
    if (open) changes.push({ ...open, before: open.before.trim(), after: open.after.trim() });
    open = null;
    gap = '';
  };

  for (const [index, segment] of segments.entries()) {
    if (segment.type === 'equal') {
      // Edits a word or two apart read as one change, but never across paragraphs.
      if (open && countWordTokens(segment.text) <= joinGap && !segment.text.includes('\n')) {
        gap += segment.text;
      } else {
        close();
      }
      continue;
    }
    if (open) {
      open.before += gap;
      open.after += gap;
      gap = '';
    } else {
      open = { before: '', after: '', first: index, last: index };
    }
    if (segment.type === 'delete') open.before += segment.text;
    else open.after += segment.text;
    open.last = index;
  }
  close();
  return changes;
}

/**
 * Groups a diff's edits into changes, each explainable on its own. Heavily edited texts are
 * grouped more coarsely, up to `max` changes.
 */
export function changesIn(segments: readonly DiffSegment[], max = MAX_EXPLAINED_CHANGES): Change[] {
  for (const joinGap of [2, 5, 12]) {
    const changes = group(segments, joinGap);
    if (changes.length <= max) return changes;
  }
  return group(segments, Number.POSITIVE_INFINITY).slice(0, max);
}

/** Where some words are in a text: from `start` up to, but not including, `end`. */
export interface Span {
  readonly start: number;
  readonly end: number;
}

/**
 * Where each change's `after` words are in the rewritten text the diff was made from, in order,
 * so other words can be swapped in for them. A removal's span is empty.
 */
export function afterSpans(segments: readonly DiffSegment[], changes: readonly Change[]): Span[] {
  // Where each segment starts in the rewritten text, which deletions take no room in.
  const starts: number[] = [];
  let offset = 0;
  for (const { type, text } of segments) {
    starts.push(offset);
    if (type !== 'delete') offset += text.length;
  }
  return changes.map(({ first, last, after }) => {
    // The change's stretch of the rewritten text, of which `after` is the trimmed words.
    const raw = segments
      .slice(first, last + 1)
      .filter(({ type }) => type !== 'delete')
      .map(({ text }) => text)
      .join('');
    const start = (starts[first] ?? offset) + (after ? raw.length - raw.trimStart().length : 0);
    return { start, end: start + after.length };
  });
}

/**
 * The text with some of its spans' words replaced: `choices` maps a span's index to the words
 * to put there. Spans must be in order and not overlap, as afterSpans gives them.
 */
export function replaceSpans(
  text: string,
  spans: readonly Span[],
  choices: ReadonlyMap<number, string>,
): string {
  let result = '';
  let from = 0;
  for (const [index, { start, end }] of spans.entries()) {
    const words = choices.get(index);
    if (words === undefined) continue;
    result += text.slice(from, start) + words;
    from = end;
  }
  return result + text.slice(from);
}
